import { Box, Stack } from '@mui/material';
import { ReactNode, useCallback, useEffect, useMemo, useState } from 'react';

import { IconButton } from '@/components/form/baseInputs/Button/IconButton';
import { ArrowLeftIcon } from '@/components/icons/ArrowLeftIcon';
import { ArrowRightIcon } from '@/components/icons/ArrowRightIcon';
import { DialogModal } from '@/components/modals/DialogModal/DialogModal';
import { NavigationDots } from '@/components/navigation/NavigationDots/NavigationDots';

import {
  FeatureAnnouncementConfig,
  FeatureAnnouncementId,
} from '../../featureAnnouncements.config';
import { useAiAssistantSimpleTasksAnnouncement } from '../AiAssistantSimpleTasksAnnouncementModal/AiAssistantSimpleTasksAnnouncementModal';
import { AnnouncementSlideContent } from '../AnnouncementModal/AnnouncementSlideContent';
import { useEstate360VisualRefreshAnnouncement } from '../Estate360VisualRefreshAnnouncementModal/Estate360VisualRefreshAnnouncementModal';
import { useImprovedAiSuggestionsAnnouncement } from '../ImprovedAiSuggestionsAnnouncementModal/ImprovedAiSuggestionsAnnouncementModal';
import { useImprovedSummaryPageAnnouncement } from '../ImprovedSummaryPageAnnouncementModal/ImprovedSummaryPageAnnouncementModal';
import { useLuminaryAiAssistantAnnouncement } from '../LuminaryAiAssistantAnnouncementModal/LuminaryAiAssistantAnnouncementModal';
import { useNjInheritanceTaxAnnouncement } from '../NjInheritanceTaxAnnouncementModal/NjInheritanceTaxAnnouncementModal';
import { usePaInheritanceTaxAnnouncement } from '../PaInheritanceTaxAnnouncementModal/PaInheritanceTaxAnnouncementModal';
import { useTrustSummariesAnnouncement } from '../TrustSummariesAnnouncementModal/TrustSummariesAnnouncementModal';
import { useValidationQuestionsAnnouncement } from '../ValidationQuestionsAnnouncementModal/ValidationQuestionsAnnouncementModal';

interface SlideData {
  imageUrl: string;
  imageAlt: string;
  bodyText: ReactNode;
}

interface AnnouncementDataBase {
  heading: string;
  actionButtons: ReactNode;
}

interface SingleSlideAnnouncementData extends AnnouncementDataBase, SlideData {
  slides?: undefined;
}

interface MultiSlideAnnouncementData extends AnnouncementDataBase {
  slides: SlideData[];
}

type AnnouncementData =
  | SingleSlideAnnouncementData
  | MultiSlideAnnouncementData;

interface CarouselStep {
  announcementId: FeatureAnnouncementId;
  heading: string;
  imageUrl: string;
  imageAlt: string;
  bodyText: ReactNode;
  actionButtons: ReactNode;
}

interface FeatureAnnouncementCarouselModalProps {
  isOpen: boolean;
  onClose: (viewedAnnouncementIds: FeatureAnnouncementId[]) => void;
  announcements: FeatureAnnouncementConfig[];
  householdId: string;
}

export function FeatureAnnouncementCarouselModal({
  isOpen,
  onClose,
  announcements,
  householdId,
}: FeatureAnnouncementCarouselModalProps) {
  const [activeStep, setActiveStep] = useState(0);
  // Track which announcements have been viewed (displayed to the user)
  const [viewedIds, setViewedIds] = useState<Set<FeatureAnnouncementId>>(
    new Set()
  );

  const handleClose = useCallback(() => {
    onClose(Array.from(viewedIds));
  }, [onClose, viewedIds]);

  const estate360VisualRefreshData = useEstate360VisualRefreshAnnouncement({
    onClose: handleClose,
    householdId,
  });

  const improvedAiSuggestionsData = useImprovedAiSuggestionsAnnouncement({
    onClose: handleClose,
    householdId,
  });

  const paInheritanceTaxData = usePaInheritanceTaxAnnouncement({
    onClose: handleClose,
    householdId,
  });

  const njInheritanceTaxData = useNjInheritanceTaxAnnouncement({
    onClose: handleClose,
    householdId,
  });

  const luminaryAiAssistantData = useLuminaryAiAssistantAnnouncement({
    onClose: handleClose,
  });

  const validationQuestionsData = useValidationQuestionsAnnouncement();

  const aiAssistantSimpleTasksData = useAiAssistantSimpleTasksAnnouncement();

  const trustSummariesData = useTrustSummariesAnnouncement();

  const improvedSummaryPageData = useImprovedSummaryPageAnnouncement({
    onClose: handleClose,
    householdId,
  });

  const dataMap: Record<FeatureAnnouncementId, AnnouncementData> = useMemo(
    () => ({
      [FeatureAnnouncementId.IMPROVED_SUMMARY_PAGE_2026_10_28]:
        improvedSummaryPageData,
      [FeatureAnnouncementId.TRUST_SUMMARIES_2026_10_21]: trustSummariesData,
      [FeatureAnnouncementId.AI_ASSISTANT_SIMPLE_TASKS_2026_09_18]:
        aiAssistantSimpleTasksData,
      [FeatureAnnouncementId.VALIDATION_QUESTIONS_2026_09_04]:
        validationQuestionsData,
      [FeatureAnnouncementId.ESTATE360_VISUAL_REFRESH_2026_06_02]:
        estate360VisualRefreshData,
      [FeatureAnnouncementId.IMPROVED_AI_SUGGESTIONS_2026_07_08]:
        improvedAiSuggestionsData,
      [FeatureAnnouncementId.PA_INHERITANCE_TAX_2026_07_30]:
        paInheritanceTaxData,
      [FeatureAnnouncementId.NJ_INHERITANCE_TAX_2026_08_18]:
        njInheritanceTaxData,
      [FeatureAnnouncementId.LUMINARY_AI_ASSISTANT_2026_07_30]:
        luminaryAiAssistantData,
    }),
    [
      aiAssistantSimpleTasksData,
      estate360VisualRefreshData,
      improvedAiSuggestionsData,
      improvedSummaryPageData,
      luminaryAiAssistantData,
      njInheritanceTaxData,
      paInheritanceTaxData,
      trustSummariesData,
      validationQuestionsData,
    ]
  );

  // Flatten announcements into carousel steps, expanding multi-slide announcements
  const steps: CarouselStep[] = useMemo(() => {
    return announcements.flatMap((announcement) => {
      const data = dataMap[announcement.id];
      if (!data) return [];

      if (data.slides) {
        return data.slides.map((slide) => ({
          announcementId: announcement.id,
          heading: data.heading,
          actionButtons: data.actionButtons,
          ...slide,
        }));
      }

      return [
        {
          announcementId: announcement.id,
          heading: data.heading,
          imageUrl: data.imageUrl,
          imageAlt: data.imageAlt,
          bodyText: data.bodyText,
          actionButtons: data.actionButtons,
        },
      ];
    });
  }, [announcements, dataMap]);

  const maxSteps = steps.length;
  const currentStep = steps[activeStep];

  // Mark current announcement as viewed when step changes
  useEffect(() => {
    if (currentStep?.announcementId) {
      setViewedIds((prev) => {
        const newSet = new Set(prev);
        newSet.add(currentStep.announcementId);
        return newSet;
      });
    }
  }, [currentStep?.announcementId]);

  const handleNext = () => {
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  if (!currentStep) return null;

  const handleStepClick = (step: number) => {
    setActiveStep(step);
  };

  const renderNavigation = maxSteps > 1;

  return (
    <DialogModal
      isOpen={isOpen}
      onClose={handleClose}
      heading={currentStep.heading}
      spacingPxNumber={24}
      disableBackdropClick
      dialogPaperSx={{
        minWidth: 680,
        borderRadius: '12px',
      }}
      dialogHeadingContainerSx={{
        width: '100%',
        textAlign: 'center',
        pb: 3,
        paddingRight: '24px',
      }}
      dialogContentSx={{
        padding: '8px 24px 0px 24px',
      }}
      dialogActionsSx={{
        pt: 0,
        display: 'block',
      }}
      dialogTitleProps={{
        variant: 'title-xl',
      }}
      actions={
        <Stack width="100%" spacing={3} alignItems="center">
          {renderNavigation && (
            <NavigationDots
              steps={maxSteps}
              activeStepIndex={activeStep}
              onStepClick={handleStepClick}
            />
          )}
          <Stack
            width="100%"
            direction="row"
            spacing={1}
            justifyContent="flex-end"
            alignItems="center"
          >
            {currentStep.actionButtons}
          </Stack>
        </Stack>
      }
    >
      <Stack direction="row" alignItems="center" spacing={1}>
        <Box sx={{ visibility: renderNavigation ? 'visible' : 'hidden' }}>
          <IconButton
            size="xs"
            onClick={handleBack}
            disabled={activeStep === 0}
            variant="transparent"
            icon={ArrowLeftIcon}
            ariaLabel="Back"
          />
        </Box>
        <Box minHeight={425}>
          <AnnouncementSlideContent
            imageUrl={currentStep.imageUrl}
            imageAlt={currentStep.imageAlt}
            bodyText={currentStep.bodyText}
          />
        </Box>
        <Box sx={{ visibility: renderNavigation ? 'visible' : 'hidden' }}>
          <IconButton
            size="xs"
            onClick={handleNext}
            disabled={activeStep === maxSteps - 1}
            variant="transparent"
            icon={ArrowRightIcon}
            ariaLabel="Next"
          />
        </Box>
      </Stack>
    </DialogModal>
  );
}
